/**
 * @typedef {Object} WalletState
 * @property {string} walletAddress - The connected wallet address
 * @property {boolean} isConnected - Whether wallet is connected
 * @property {boolean} hasRequiredTokens - Whether wallet has minimum required tokens
 * @property {number} tokenBalance - Current token balance
 * @property {boolean} isVerifying - Whether balance verification is in progress
 * @property {string|null} lastBalanceCheck - Timestamp of last balance check
 * @property {boolean} isLoading - Whether any wallet operation is in progress
 * @property {string|null} error - Current error message if any
 */

/**
 * @typedef {Object} WalletConnection
 * @property {string} id - Unique connection ID
 * @property {string} wallet_address - Wallet address
 * @property {string|null} display_name - User display name
 * @property {string|null} avatar_url - User avatar URL
 * @property {number} token_balance - Current token balance
 * @property {string} first_connected_at - First connection timestamp
 * @property {string} last_connected_at - Last connection timestamp
 * @property {string} last_balance_check - Last balance check timestamp
 * @property {boolean} is_active - Whether connection is active
 * @property {boolean} is_admin - Whether user has admin privileges
 * @property {string} xrpl_network - XRPL network (mainnet/testnet)
 * @property {Object} metadata - Additional metadata
 */

/**
 * @typedef {Object} XummPayload
 * @property {string} uuid - Payload unique identifier
 * @property {Object} next - Navigation URLs
 * @property {string} next.always - XUMM app URL
 * @property {Object} refs - Reference URLs
 * @property {string} refs.qr_png - QR code image URL
 * @property {string} refs.qr_matrix - QR code matrix data
 * @property {string} refs.qr_uri_quality_opts - QR code URI options
 * @property {string} refs.websocket_status - WebSocket status URL
 */

/**
 * @typedef {Object} XummPayloadStatus
 * @property {boolean} success - Whether operation was successful
 * @property {string|null} account - Wallet address if signed
 * @property {string} status - Status: 'resolved' | 'unresolved' | 'expired'
 * @property {boolean} signed - Whether payload was signed
 * @property {boolean} expired - Whether payload expired
 * @property {Object|null} response - Full XUMM response
 */

/**
 * @typedef {Object} AuthSession
 * @property {string} sessionId - Unique session identifier
 * @property {string} walletAddress - Connected wallet address
 * @property {number} expiresAt - Session expiration timestamp
 * @property {string} createdAt - Session creation timestamp
 * @property {Object} metadata - Session metadata
 */

/**
 * @typedef {Object} WalletConnectionResult
 * @property {boolean} success - Whether connection was successful
 * @property {string|null} walletAddress - Connected wallet address
 * @property {string|null} error - Error message if failed
 * @property {XummPayload|null} payload - XUMM payload data
 */

/**
 * @typedef {Object} TokenVerificationResult
 * @property {boolean} hasRequiredTokens - Whether wallet has minimum tokens
 * @property {number} currentBalance - Current token balance
 * @property {number} requiredBalance - Required minimum balance
 * @property {string} currencyCode - Token currency code
 * @property {string} issuer - Token issuer address
 */

/**
 * Storage keys for wallet data persistence
 */
export const STORAGE_KEYS = {
  WALLET_ADDRESS: 'xgc_wallet_address',
  WALLET_CONNECTED: 'xgc_wallet_connected',
  XUMM_PAYLOAD_ID: 'xgc_xumm_payload_id',
  LAST_BALANCE_CHECK: 'xgc_last_balance_check',
  SESSION_ID: 'xgc_session_id',
  AUTH_TOKEN: 'xgc_auth_token'
};

/**
 * Wallet connection status constants
 */
export const WALLET_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  VERIFYING: 'verifying',
  ERROR: 'error'
};

/**
 * XUMM payload status constants
 */
export const PAYLOAD_STATUS = {
  PENDING: 'pending',
  RESOLVED: 'resolved',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled'
};

/**
 * Network configuration constants
 */
export const NETWORKS = {
  MAINNET: 'wss://s1.ripple.com',
  TESTNET: 'wss://xrplcluster.com'
};

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG = {
  SESSION_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days
  BALANCE_CHECK_INTERVAL: 30 * 60 * 1000, // 30 minutes
  PAYLOAD_TIMEOUT: 10 * 60 * 1000, // 10 minutes
  POPUP_DIMENSIONS: {
    width: 500,
    height: 700
  }
}; 